import * as MuiColors from "@mui/material/colors";
import { rgbToHex } from "@mui/material/styles";
import { arrayFlat, arraySum } from "common/utilities/core/array";
import type { CSSProperties } from "react";

import type { Color, HexColor } from "./colors.types";

/**
 * RGB array to CSS RGB
 * i.e. #123456 => rgb(18, 52, 86)
 */
export const rgbToCSS = (color: Color) => `rgb(${color.join(",")})`;
export const CSSToRgb = (css: string): Color =>
  css.split(",").map((v) => Number((v.match(/[0-9]+/g) as string[])[0])) as Color;

/**
 * Used to evaluate if a background color needs bright or dark text to be readable
 * Note: same logic as backend ColorUtil.luminanceMeasure
 */
export function isDarkColor(color: number[]) {
  // http://stackoverflow.com/a/1855903
  // counting the perceptive luminance, human eye favors green color (see HSL)
  const luminance = 1 - (color[0] * 0.299 + color[1] * 0.587 + color[2] * 0.114) / 255;
  return luminance > 0.5;
}

export function hexToRgb(hex: string): Color | undefined {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : undefined;
}

export function isDarkColorString(color?: string) {
  if (color) {
    return isDarkColor(hexToRgb(color) || []);
  }
  return false;
}

export function styleBackgroundColor(backgroundColor?: string): CSSProperties {
  const style: CSSProperties = {};
  if (backgroundColor) {
    style.backgroundColor = `${backgroundColor}`;
    if (isDarkColorString(backgroundColor)) {
      style.color = "white";
    }
  }

  return style;
}

export function getRandomRgb() {
  const num = Math.round(0xffffff * Math.random());

  const r = num >> 16;

  const g = (num >> 8) & 255;

  const b = num & 255;
  return [r, g, b] as Color;
}

export function averageRgbs(colors: Color[]): Color {
  return [
    Math.ceil(arraySum(colors.map((c) => c[0])) / colors.length),
    Math.ceil(arraySum(colors.map((c) => c[1])) / colors.length),
    Math.ceil(arraySum(colors.map((c) => c[2])) / colors.length),
  ];
}

export type Hue = keyof typeof MuiColors;

export const muiPaletteHues = [
  "amber",
  "blue",
  "brown",
  "red",
  "deepOrange",
  "teal",
  "deepPurple",
  "green",
  "indigo",
  "lightBlue",
  "orange",
  "lime",
  "pink",
  "lightGreen",
  "blueGrey",
  "purple",
  "yellow",
  "cyan",
] as Hue[];

export function muiHueFromRgbColor(color: Color) {
  const rgbColorAsCSS = rgbToCSS(color);
  const hexColor = rgbToHex(rgbColorAsCSS) as HexColor;

  // eslint-disable-next-line import/namespace
  return muiPaletteHues.find((hue) => (Object.values(MuiColors[hue]) as HexColor[]).includes(hexColor));
}

export const muiShades = [600, 400, 700, 300, 800, 200, 900, 500];

export function muiShadesFromHue(hue: Hue, usedColor?: HexColor) {
  // eslint-disable-next-line import/namespace
  const hueColors = MuiColors[hue];
  return muiShades
    .map((shade) => hueColors[shade as keyof typeof hueColors] as HexColor)
    .filter((hexColor) => usedColor !== hexColor);
}

/**
 * Using Material Design palette system implemented by MUI https://mui.com/material-ui/customization/color/
 * Extract colors based on their "readability on screen" to be used for visualizations showing features (plasmid viewer, WSA).
 */
export function huesPalette(hues: Hue[], shades?: number[]) {
  return arrayFlat(
    (shades || muiShades).map((shade) =>
      hues.map((hue) => {
        // eslint-disable-next-line import/namespace
        const hueColors = MuiColors[hue];
        return hueColors[shade as keyof typeof hueColors] as HexColor;
      })
    )
  );
}

export const defaultColorPickerPalette = huesPalette(muiPaletteHues.slice(0, 8), [200, 400, 600, 800]);
